<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { useOnixActionsStore } from 'dashboard/store/onix/actions';
import OnixPageLayout from 'dashboard/components-next/onix/OnixPageLayout.vue';
import ActionStepsForm from 'dashboard/components-next/onix/ActionStepsForm.vue';
import ActionTryPanel from 'dashboard/components-next/onix/ActionTryPanel.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();
const actionsStore = useOnixActionsStore();

const slugify = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const blankForm = () => ({
  id: null,
  title: '',
  slug: '',
  description: '',
  http_method: 'GET',
  endpoint_url: '',
  request_template: '',
  response_template: '',
  auth_type: 'none',
  enabled: true,
  token: '',
  username: '',
  password: '',
  apiKey: '',
});

const form = ref(blankForm());
const deleteDialogRef = ref(null);
const testResult = ref('');
const testVerified = ref(null);
const missing = ref(false);

const flags = computed(() => actionsStore.getUIFlags);
const isNew = computed(() => !route.params.actionId);
const backUrl = computed(() => accountScopedRoute('onix_actions_index'));
const pageTitle = computed(() =>
  isNew.value
    ? t('ONIX.ACTIONS.NEW_TITLE')
    : form.value.title || t('ONIX.ACTIONS.EDIT_TITLE')
);

const fill = action => {
  const auth = action.auth_config || {};
  form.value = {
    id: action.id,
    title: action.title || '',
    slug: action.slug || '',
    description: action.description || '',
    http_method: action.http_method || 'GET',
    endpoint_url: action.endpoint_url || '',
    request_template: action.request_template || '',
    response_template: action.response_template || '',
    auth_type: action.auth_type || 'none',
    enabled: action.enabled !== false,
    token: auth.token || '',
    username: auth.username || '',
    password: auth.password || '',
    apiKey: auth.key || '',
  };
};

const authConfig = () => {
  if (form.value.auth_type === 'bearer') return { token: form.value.token };
  if (form.value.auth_type === 'basic') {
    return { username: form.value.username, password: form.value.password };
  }
  if (form.value.auth_type === 'api_key') {
    return { key: form.value.apiKey, name: 'X-API-Key' };
  }
  return {};
};

const payload = () => ({
  action: {
    title: form.value.title.trim(),
    slug: form.value.id
      ? form.value.slug
      : slugify(form.value.title) || `accion_${Date.now()}`,
    description: form.value.description,
    http_method: form.value.http_method,
    endpoint_url: form.value.endpoint_url.trim(),
    request_template: form.value.request_template,
    response_template: form.value.response_template,
    auth_type: form.value.auth_type,
    auth_config: authConfig(),
    enabled: form.value.enabled,
  },
});

const save = async () => {
  if (!form.value.title.trim() || !form.value.endpoint_url.trim()) {
    useAlert(t('ONIX.ACTIONS.ERROR_REQUIRED'));
    return;
  }
  try {
    const data = form.value.id
      ? await actionsStore.update(form.value.id, payload())
      : await actionsStore.create(payload());
    if (!data?.id) {
      useAlert(t('ONIX.ACTIONS.ERROR_SAVE'));
      return;
    }
    useAlert(t('ONIX.ACTIONS.SAVED'));
    if (!form.value.id) {
      router.replace(
        accountScopedRoute('onix_actions_edit', { actionId: data.id })
      );
    }
    fill(data);
  } catch {
    useAlert(t('ONIX.ACTIONS.ERROR_SAVE'));
  }
};

const confirmDelete = async () => {
  const ok = await actionsStore.remove(form.value.id);
  deleteDialogRef.value?.close();
  useAlert(ok ? t('ONIX.ACTIONS.DELETED') : t('ONIX.ACTIONS.ERROR_SAVE'));
  if (ok) router.push(backUrl.value);
};

const runTest = async params => {
  testResult.value = '';
  try {
    const data = await actionsStore.test(form.value.id, params);
    if (!data) {
      testVerified.value = false;
      useAlert(t('ONIX.ACTIONS.ERROR_TRY'));
      return;
    }
    if (data.action) fill(data.action);
    testResult.value = data.result || '';
    testVerified.value = !!data.verified;
  } catch {
    testVerified.value = false;
    useAlert(t('ONIX.ACTIONS.ERROR_TRY'));
  }
};

onMounted(async () => {
  if (!route.params.actionId) return;
  const data = await actionsStore.fetchOne(route.params.actionId);
  if (!data) {
    missing.value = true;
    return;
  }
  fill(data);
});
</script>

<template>
  <OnixPageLayout
    :header-title="pageTitle"
    :description="t('ONIX.ACTIONS.FORM_DESCRIPTION')"
    :back-url="backUrl"
    :is-fetching="!isNew && !form.id && !missing"
    :is-empty="missing"
  >
    <template #actions>
      <Button
        v-if="form.id && isAdmin"
        :label="t('ONIX.ACTIONS.DELETE')"
        color="ruby"
        variant="faded"
        size="sm"
        @click="deleteDialogRef?.open()"
      />
    </template>
    <template #emptyState>
      <p class="text-sm text-n-slate-11">
        {{ t('ONIX.ACTIONS.MISSING') }}
      </p>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-2xl pb-10">
        <ActionStepsForm v-model="form" :disabled="!isAdmin" />
        <Button
          :label="t('ONIX.ACTIONS.SAVE')"
          size="sm"
          class="self-start"
          :disabled="!isAdmin"
          :is-loading="flags.creatingItem || flags.updatingItem"
          @click="save"
        />
        <ActionTryPanel
          v-if="form.id"
          :endpoint-url="form.endpoint_url"
          :request-template="form.request_template"
          :is-loading="flags.testingItem"
          :tried="testVerified !== null"
          :succeeded="testVerified === true"
          :result="testResult"
          @try="runTest"
        />
      </div>
    </template>
  </OnixPageLayout>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('ONIX.ACTIONS.DELETE_TITLE')"
    :description="t('ONIX.ACTIONS.DELETE_DESCRIPTION')"
    :confirm-button-label="t('ONIX.ACTIONS.DELETE')"
    :is-loading="flags.deletingItem"
    @confirm="confirmDelete"
  />
</template>
